import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Badge } from 'primereact/badge';
import type { MissingFieldsReport } from '../../bidAnalysisTemplate.logic';
import classes from './MissingFieldsModal.module.scss';

interface MissingFieldsModalProps {
  visible: boolean;
  onHide: () => void;
  report: MissingFieldsReport | null;
}

export const MissingFieldsModal: React.FC<MissingFieldsModalProps> = ({
  visible,
  onHide,
  report,
}) => {
  if (!report) return null;

  const hasIssues = report.globalIssues.length > 0 || report.combinationReports.length > 0;

  const renderHeader = () => (
    <div className={classes.headerContent}>
      <div className={classes.headerTitle}>
        <i className="pi pi-list-check" style={{ marginRight: '0.5rem' }} />
        <span>Missing Fields Report</span>
      </div>
      <div className={classes.headerStats}>
        <div className={classes.stat}>
          <span className={classes.statLabel}>Total Fields:</span>
          <span className={classes.statValue}>{report.totalFields}</span>
        </div>
        <div className={classes.stat}>
          <span className={classes.statLabel}>Completed:</span>
          <span className={classes.statValue} style={{ color: '#22c55e' }}>
            {report.completedFields}
          </span>
        </div>
        <div className={classes.stat}>
          <span className={classes.statLabel}>Missing:</span>
          <span className={classes.statValue} style={{ color: '#ef4444' }}>
            {report.missingFields}
          </span>
        </div>
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className={classes.footer}>
      <Button
        label="Close"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={renderHeader()}
      footer={renderFooter()}
      className={classes.missingFieldsModal}
      style={{ width: '90vw', maxWidth: '900px' }}
      modal
      dismissableMask
    >
      <div className={classes.modalContent}>
        {/* Progress Overview */}
        <div className={classes.progressSection}>
          <div className={classes.progressHeader}>
            <span className={classes.progressLabel}>Overall Progress</span>
            <span className={classes.progressValue}>{report.progressPercent}%</span>
          </div>
          <ProgressBar value={report.progressPercent} showValue={false} />
        </div>

        {/* No Issues Message */}
        {!hasIssues && (
          <div className={classes.noIssues}>
            <i className="pi pi-check-circle" style={{ fontSize: '3rem', color: '#22c55e' }} />
            <h3>All Fields Complete!</h3>
            <p>All required fields have been filled for all molecules, pack sizes, and years.</p>
          </div>
        )}

        {/* Global Issues */}
        {report.globalIssues.length > 0 && (
          <div className={classes.section}>
            <div className={classes.sectionHeader}>
              <i className="pi pi-exclamation-triangle" style={{ color: '#ef4444' }} />
              <span>Project Information Issues</span>
              <Badge value={report.globalIssues.length} severity="danger" />
            </div>
            <ul className={classes.issueList}>
              {report.globalIssues.map((issue, index) => (
                <li key={index} className={classes.issueItem}>
                  <i className="pi pi-times-circle" style={{ color: '#ef4444' }} />
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Combination Issues */}
        {report.combinationReports.length > 0 && (
          <div className={classes.section}>
            <div className={classes.sectionHeader}>
              <i className="pi pi-list" style={{ color: '#f59e0b' }} />
              <span>Missing Fields by Molecule & Pack Size</span>
              <Badge value={report.combinationReports.length} severity="warning" />
            </div>
            
            <Accordion multiple className={classes.accordion}>
              {report.combinationReports.map((combo, comboIndex) => {
                const totalMissing = combo.yearReports.reduce(
                  (sum, year) => sum + year.missingFields.length,
                  0
                );
                
                return (
                  <AccordionTab
                    key={comboIndex}
                    header={
                      <div className={classes.accordionHeader}>
                        <div className={classes.accordionTitle}>
                          <i className="pi pi-tag" />
                          <span className={classes.conceptName}>{combo.conceptName}</span>
                          <span className={classes.separator}>•</span>
                          <span className={classes.packSize}>Pack Size: {combo.packSize}</span>
                        </div>
                        <Badge value={`${totalMissing} missing`} severity="warning" />
                      </div>
                    }
                  >
                    <div className={classes.yearReports}>
                      {combo.yearReports.map((yearReport, yearIndex) => (
                        <div key={yearIndex} className={classes.yearReport}>
                          <div className={classes.yearHeader}>
                            <span className={classes.yearLabel}>
                              <i className="pi pi-calendar" />
                              {yearReport.yearLabel}
                            </span>
                            <span className={classes.yearProgress}>
                              {yearReport.completedFields}/{yearReport.totalFields} completed
                            </span>
                          </div>
                          <ul className={classes.fieldList}>
                            {yearReport.missingFields.map((field, fieldIndex) => (
                              <li key={fieldIndex} className={classes.fieldItem}>
                                <i className="pi pi-minus-circle" style={{ color: '#ef4444' }} />
                                <span>{field}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </AccordionTab>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>
    </Dialog>
  );
};

